<template>
    <span>
        <div class="modal" :class="{ 'is-active': isShowingModal }">
            <div class="modal-background" @click.stop.prevent />
            <div class="modal-content">
                <div class="box log-modal-box">
                    <div class="log-header">
                        <div>
                            <h1 class="title is-4">{{ title }}</h1>
                            <p class="subtitle is-6">
                                {{ filteredMessages.length }} / {{ normalizedMessages.length }} entries
                            </p>
                        </div>

                        <div class="log-header-actions" v-if="normalizedMessages.length">
                            <button
                                type="button"
                                class="button is-success is-rounded log-export-button"
                                :disabled="exporting"
                                @click="exportXLSX">
                                <span class="icon is-small">
                                    <i class="fas fa-file-excel" />
                                </span>
                                <span>{{ exporting ? 'Exporting...' : 'Export log XLSX' }}</span>
                            </button>
                            <div class="log-export-hint">
                                Exports the currently filtered rows.
                            </div>
                            <div class="log-summary">
                                <span class="log-pill is-info">
                                    {{ summary.info }} info
                                </span>
                                <span class="log-pill is-warning" v-if="summary.warning">
                                    {{ summary.warning }} warnings
                                </span>
                                <span class="log-pill is-danger" v-if="summary.error">
                                    {{ summary.error }} errors
                                </span>
                                <span class="log-pill is-muted" v-if="summary.other">
                                    {{ summary.other }} other
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="field" v-if="normalizedMessages.length">
                        <div class="control">
                            <input
                                v-model.trim="searchQuery"
                                class="input"
                                type="text"
                                placeholder="Search log entries"
                            >
                        </div>
                    </div>

                    <div class="log-table" v-if="filteredMessages.length">
                        <div class="log-table-head">
                            <div>Level</div>
                            <div>Message</div>
                            <div>Details</div>
                        </div>
                        <div
                            v-for="(message, index) in filteredMessages"
                            :key="`${index}-${message.raw}`"
                            class="log-row"
                            :class="`is-${message.level}`"
                        >
                            <div class="log-level-cell">
                                <span class="log-badge" :class="`is-${message.level}`">
                                    {{ message.levelLabel }}
                                </span>
                            </div>
                            <div class="log-message-cell">
                                <div class="log-title">{{ message.title }}</div>
                                <div v-if="message.action" class="log-action">
                                    {{ message.action }}
                                </div>
                            </div>
                            <div
                                class="log-details-cell"
                                v-html="$options.filters.n2br(message.details)"
                            />
                        </div>
                    </div>

                    <div class="notification is-light" v-else-if="normalizedMessages.length">
                        No log entries match the current search.
                    </div>

                    <div class="notification is-light" v-else>
                        No log entries.
                    </div>

                    <div class="columns">
                        <div class="column">
                            <button class="button is-large is-fullwidth" @click.stop.prevent="close">
                                Ok
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <button class="modal-close" @click.stop.prevent="close" />
        </div>
    </span>
</template>

<style lang="stylus" scoped>
    .modal-content
        max-width 84vw
        min-width 720px
        width 100%
        @media screen and (min-width: 1280px)
            max-width 1120px
        @media screen and (max-width: 800px)
            min-width initial
            max-width 94vw

    .log-modal-box
        padding 1.5rem

    .log-header
        display flex
        justify-content space-between
        align-items flex-start
        gap 1rem
        margin-bottom 1rem
        @media screen and (max-width: 800px)
            flex-direction column

    .log-summary
        display flex
        gap .5rem
        flex-wrap wrap

    .log-header-actions
        display flex
        flex-direction column
        align-items flex-end
        gap .35rem
        @media screen and (max-width: 800px)
            align-items flex-start

    .log-export-button
        font-weight 700
        box-shadow 0 8px 18px rgba(16, 185, 129, .18)

    .log-export-hint
        color #667085
        font-size .72rem
        margin-bottom .25rem

    .log-pill
        display inline-flex
        align-items center
        padding .35rem .7rem
        border-radius 999px
        font-size .8rem
        font-weight 700
        &.is-info
            background #ebf5ff
            color #165fa7
        &.is-warning
            background #fff6db
            color #8b5e00
        &.is-danger
            background #feebee
            color #b42318
        &.is-muted
            background #f3f4f6
            color #4b5563

    .log-table
        border 1px solid #e5e7eb
        border-radius 14px
        overflow hidden
        margin-bottom 1rem

    .log-table-head
        display grid
        grid-template-columns 120px 240px 1fr
        gap 1rem
        padding .65rem .9rem
        background #f8fafc
        border-bottom 1px solid #e5e7eb
        font-size .68rem
        font-weight 700
        text-transform uppercase
        letter-spacing .04em
        color #475467
        @media screen and (max-width: 800px)
            display none

    .log-row
        display grid
        grid-template-columns 120px 240px 1fr
        gap 1rem
        padding .56rem .8rem
        border-bottom 1px solid #eef2f6
        align-items start
        &:last-child
            border-bottom none
        &.is-warning
            background #fffdf5
        &.is-error
            background #fff7f7
        @media screen and (max-width: 800px)
            grid-template-columns 1fr
            gap .4rem

    .log-badge
        display inline-flex
        align-items center
        justify-content center
        min-width 72px
        padding .28rem .55rem
        border-radius 999px
        font-size .68rem
        font-weight 700
        text-transform uppercase
        letter-spacing .04em
        &.is-info
            background #ecfdf3
            color #067647
        &.is-warning
            background #fff4e5
            color #9a6700
        &.is-error
            background #fee4e2
            color #b42318
        &.is-other
            background #f2f4f7
            color #475467

    .log-title
        font-weight 700
        font-size .92rem
        color #101828
        line-height 1.1

    .log-action
        margin-top .1rem
        font-size .66rem
        font-weight 600
        color #475467
        text-transform uppercase
        letter-spacing .04em

    .log-details-cell
        color #344054
        font-size .88rem
        line-height 1.18
        word-break break-word
</style>

<script>
    import BaseModal from '@lassehaslev/vue-modal'
    import VenueParticipationService from 'VenueParticipation/service'

    export default {
        mixins: [ BaseModal ],
        props: {
            title: {
                type: String,
                default: 'Import Log'
            },
            exportFilenamePrefix: {
                type: String,
                default: 'import-log'
            }
        },
        data() {
            return {
                messages: [],
                searchQuery: '',
                exporting: false
            }
        },
        computed: {
            normalizedMessages() {
                return (this.messages || []).map(this.normalizeMessage)
            },
            filteredMessages() {
                const query = (this.searchQuery || '').toLowerCase()
                if (!query)
                    return this.normalizedMessages

                return this.normalizedMessages.filter((entry) =>
                    [entry.levelLabel, entry.title, entry.action, entry.details, entry.raw]
                        .filter(Boolean)
                        .some(value => String(value).toLowerCase().includes(query))
                )
            },
            summary() {
                return this.filteredMessages.reduce((acc, entry) => {
                    acc[entry.level] += 1
                    return acc
                }, {
                    info: 0,
                    warning: 0,
                    error: 0,
                    other: 0
                })
            }
        },
        methods: {
            normalizeMessage(rawMessage) {
                const raw = rawMessage === null || rawMessage === undefined
                    ? ''
                    : String(rawMessage)

                let level = 'info'
                if (/\[warning\]|\[!\]|warning/i.test(raw))
                    level = 'warning'
                if (/\[error\]|\[failed\]|error/i.test(raw))
                    level = 'error'

                const colonIndex = raw.indexOf(':')
                const prefix = colonIndex >= 0 ? raw.slice(0, colonIndex).trim() : raw.trim()
                const suffix = colonIndex >= 0 ? raw.slice(colonIndex + 1).trim() : ''

                const bracketMatches = prefix.match(/\[[^\]]+\]/g) || []
                const action = bracketMatches.length
                    ? bracketMatches[bracketMatches.length - 1].replace(/\[|\]/g, '')
                    : ''

                const title = prefix.replace(/\[[^\]]+\]/g, '').trim() || 'Log entry'
                const details = suffix || raw

                return {
                    raw,
                    level,
                    levelLabel: level === 'error' ? 'Error' : level === 'warning' ? 'Warning' : 'Info',
                    title,
                    action,
                    details
                }
            },
            onModalOpen() {
                this.searchQuery = ''
            },
            async exportXLSX() {
                this.exporting = true
                try {
                    const response = await VenueParticipationService.exportImportLogXLSX(this.filteredMessages)
                    const blob = response instanceof Blob ? response : new Blob([response])
                    const href = URL.createObjectURL(blob)
                    const link = Object.assign(document.createElement('a'), {
                        href,
                        download: `${this.exportFilenamePrefix}-${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`
                    })
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(href)
                }
                finally {
                    this.exporting = false
                }
            }
        }
    }
</script>
